<i18n>
  {
    "en": {
      "serialNumber": "Serial Number"
    },
    "ru": {
      "serialNumber": "Серийный номер"
    }
  }
</i18n>

<template>
  <div>
    <div class="content-block dx-card responsive-paddings">
      <div v-if="deviceDays" class="header-device">
        <div class="information">
          <div>
            <span class="title">{{deviceDays.device.modelCode}}</span>
            <span class="type">{{deviceType()}}</span>
          </div>

          <form-switch-item
            :item-value="deviceDays.device.isActive"
            :label="$t('device.detail.managed')"
            :is-loading="isFetchingMainFlag"
            :disabled="!canServed"
            @change="onChangeMainFlag"
          />
        </div>

        <div class="information">
          <div class="subtitle">
            <span v-if="deviceDays.pixIp">
              <a class="link" v-bind:href="'https://'+ deviceDays.pixIp" target="_blank" rel="noopener noreferrer">{{deviceDays.pixIp}}</a>
            </span>
            <span v-if="deviceDays.device.serialNumber">, {{deviceDays.device.serialNumber}}</span>
          </div>

          <div class="info-panel">
            <div class="label">{{$t('device.detail.statusName')}}:</div>
            <div v-if="deviceDays.device.status" :class="getClassStatus()">{{deviceDays.device.status.name}}</div>

            <DxButton
              icon="info"
              @click="togglePopup"
            />
          </div>
        </div>

        <DxPopup
          :visible="isPopupVisible"
          :drag-enabled="false"
          :hide-on-outside-click="true"
          :show-title="false"
          :width="380"
          height="auto"
          @hiding="onHiding"
          @showing="onShowing"
        >
          <DxPosition at="right bottom" my="right top" of=".info-panel" />

          <div class="group">
            <div class="field" v-for="item in infoPanel[0]" v-bind:key="item.label">
              <div class="label">{{item.label}}</div>
              <div class="value">
                <div :class="{'optional': item.optional}">{{item.value}}</div>
                <div v-if="item.optional">{{item.optional}}</div>
              </div>
            </div>
          </div>

          <div class="group">
            <div class="field" v-for="item in infoPanel[1]" v-bind:key="item.label">
              <div class="label">{{item.label}}</div>
              <div class="value">{{item.value}}</div>
            </div>
          </div>

        </DxPopup>
      </div>

      <dx-tab-panel
        :data-source="tabViews"
        :selected-index.sync="selectedIndex"
        :loop="false"
        :animation-enabled="false"
        @selection-changed="onSelectionChanged"
      >
        <template #title="view">
          <span>{{ view.data.title }}</span>
        </template>

        <template #item="view">
          <div class="tabpanel-item">
            <component
              v-bind:is="view.data.id"
              :device-days="deviceDays"
              :id="id"
              :locations="view.data.id === 'device-information' ? locations : undefined"
            >
            </component>
          </div>
        </template>
      </dx-tab-panel>
    </div>
  </div>
</template>

<script>
import baseView from "../base.vue";
import appInfo from "../../app-info";

import { mapCacheActions } from "vuex-cache";

import { DxSwitch } from 'devextreme-vue/switch';
import { DxButton } from 'devextreme-vue/button';
import { DxPopup ,DxPosition } from 'devextreme-vue/popup';
import DxTabPanel from "devextreme-vue/tab-panel";

import { notifier } from '@/shared/lib/notifier/index';

import DeviceInformation from "./information.vue";
import DeviceDetail from "./detail";
import DeviceUserDays from "./user-days";
import DeviceHistory from "./history.vue";
import FormSwitchItem from './form-switch-item';

import { AclHandler } from "@/services/acl-handler";
import { getDeviceDays, getUserLocations, putDevice } from "@/services/api";
import { DataLocation } from "@/mixins/dataLocationMixin";

import moment from "moment";
import { cloneDeep } from 'lodash';

export default {
  props: ["id"],
  extends: baseView,
  mixins: [DataLocation],
  components: {
    DxTabPanel, DeviceInformation, DeviceDetail, DeviceUserDays, DeviceHistory, DxSwitch,
    DxButton, DxPopup, DxPosition, FormSwitchItem
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      isFetchingMainFlag: false,
      deviceDays: null,
      selectedIndex: 0,
      isPopupVisible: false,
      infoPanel: []
    };
  },
  computed: {
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    canServed() {
      return this.aclUser.hasGrantForOperation('BTN_EDIT_DEVICE', 'EXECUTE') || false;
    },
    tabViews() {
      const tab = [
        { id: "device-information", title: this.$t("device.tabs.information") },
        { id: "device-user-days", title: this.$t("device.tabs.userDays") },
        { id: "device-history", title: this.$t("device.tabs.history") }
      ];

      if (this.aclUser && this.aclUser.hasRole('DEVELOPER')) {
        tab.splice(1, 0, {
          id: "device-detail",
          title: this.$t("device.tabs.device"),
        });
      }

      return tab;
    }
  },
  methods: {
    ...mapCacheActions(["getDictionaryItems"]),
    deviceType() {
      if (this.deviceDays && this.deviceDays.device.model.dim01 && this.deviceDays.device.model.dim02 && this.deviceDays.device.model.dim03)
        return `, ${this.deviceDays.device.model.dim02.name} ${this.deviceDays.device.model.dim01.name} ${this.deviceDays.device.model.dim03.name}`;
      else
        return '';
    },
    getClassStatus() {
      if (this.deviceDays) {
        let className = 'value';

        switch (this.deviceDays.device.status && this.deviceDays.device.status.code) {
          case 'OK':
            return className += ' ok';
          case 'WARNING':
          case 'SCAN_ERROR':
            return className += ' warning';
          case 'ERROR':
          case 'NOT_AVAILABLE':
            return className += ' error';
          case 'NOT_SERVICED':
            return className += ' not-serviced';
        
          default:
            return className;
        }
      }
    },
    togglePopup() {
      this.isPopupVisible = true;
    },
    onShowing(e) {
      e.component.content().classList.add("info-device");
    },
    onHiding(){
      this.isPopupVisible = false;
    },
    async onChangeMainFlag(value) {
      const data = cloneDeep(this.deviceDays.device);
      data.isActive = value;

      this.isFetchingMainFlag = true;
      try {
        this.deviceDays.device = await putDevice(this.id, data);
        notifier.showSuccess(this.$t('general.updateSuccess'));
      } catch (err) {
        this.onDataError(err);
      } finally {
        this.isFetchingMainFlag = false;
      }
    },
    async loadDevice(id) {
      let breadcrumb = [{
        link: `/devices`,
        title: this.$t("devices.title")
      }];

      try {
        this.locations = await getUserLocations();

        if (id) {
          let [ deviceDays ] = await Promise.all([
            getDeviceDays(id)
          ]);
          if (typeof deviceDays.lixBlackTonerLevel === "number")
            deviceDays.lixBlackTonerLevelText = `${deviceDays.lixBlackTonerLevel}%`;
          if (typeof deviceDays.lixCyanTonerLevel === "number")
            deviceDays.lixCyanTonerLevelText = `${deviceDays.lixCyanTonerLevel}%`;
          if (typeof deviceDays.lixMagentaTonerLevel === "number")
            deviceDays.lixMagentaTonerLevelText = `${deviceDays.lixMagentaTonerLevel}%`;
          if (typeof deviceDays.lixYellowTonerLevel === "number")
            deviceDays.lixYellowTonerLevelText = `${deviceDays.lixYellowTonerLevel}%`;

          this.infoPanel[0] = [
            {
              label: this.$t('device.detail.createDate'),
              value: deviceDays.device.createDate ? moment(deviceDays.device.createDate).format('DD.MM.YYYY') : deviceDays.device.createDate,
              optional: moment(deviceDays.device.createDate).format('HH:mm:ss')
            },
            {
              label: this.$t('device.detail.walkRefreshDate'),
              value: deviceDays.device.walkRefreshDate ? moment(deviceDays.device.walkRefreshDate).format('DD.MM.YYYY') : deviceDays.device.walkRefreshDate,
              optional: moment(deviceDays.device.walkRefreshDate).format('HH:mm:ss')
            },
            {
              label: this.$t('device.detail.refreshDate'),
              value: deviceDays.device.refreshDate ? moment(deviceDays.device.refreshDate).format('DD.MM.YYYY') : deviceDays.device.refreshDate,
              optional: moment(deviceDays.device.refreshDate).format('HH:mm:ss')
            },
            {
              label: this.$t('device.detail.statusName'),
              value: deviceDays.device.status ? deviceDays.device.status.name : null
            },
            {
              label: this.$t('device.detail.printStatus'),
              value: deviceDays.pixPrinterStatus ? deviceDays.pixPrinterStatus : null
            }
          ].filter(el => el.value);

          this.infoPanel[1] = [
            {
              label: this.$t('serialNumber'),
              value: deviceDays.device.serialNumber
            },
            {
              label: this.$t('device.detail.location'),
              value: deviceDays.lixLocation
            },
            {
              label: this.$t('device.detail.host'),
              value: deviceDays.pixHostname
            },
            {
              label: this.$t('device.detail.mac'),
              value: deviceDays.lixMac
            },
            {
              label: this.$t('device.detail.ip'),
              value: deviceDays.pixIp
            },
            {
              label: this.$t('device.detail.port'),
              value: deviceDays.pixPort
            }
          ].filter(el => el.value);

          this.deviceDays = deviceDays;

          let fullPathLocation = this.getFullPath(this.deviceDays.device.location.id);
          fullPathLocation = fullPathLocation.map((location) => {
            return {
              link: `/location/${location.id}`,
              title: location.name
            };
          });

          breadcrumb = breadcrumb.concat(fullPathLocation).concat({
            link: `/device/${this.id}`,
            title: `${this.deviceDays.pixIp}`
          });

          breadcrumb = breadcrumb.map(el => {
            const locationID = el.link.split('/')[2];
            if (Number(locationID) === this.deviceDays.device.location.id)
              el.deviceID = this.deviceDays.device.id;

            return el;
          });
        } else {
          this.deviceDays = { device: {}};
        }
      } catch(err) {
        this.onDataError(err);
      }

      this.$store.commit("setBreadcrumb", breadcrumb);
    },
    onSelectionChanged(e) {
      try {
        const query = {...this.$route.query};
        switch (e.addedItems[0].id) {
          case "device-information":
            delete query.tab;
            break;
          default:
            query.tab = e.addedItems[0].id;
        }

          this.$router.replace({ query }).catch(()=>{});
      } catch(ignore) {
        // eslint-disable-next-line
        console.debug(ignore);
      }
    }
  },
  async created() {
    document.title = `${appInfo.title} - ${this.$t("device.title")}`;

    await this.loadDevice(this.id);
  },
  watch: {
    id(id) {
      this.selectedIndex = 0;
      this.loadDevice(id);
    }
  }
};
</script>

<style lang="scss">
.header-device {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;

  .information {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .title {
      font-size: 24px;
      font-weight: 500;
    }

    .type {
      font-size: 24px;
    }

    .subtitle {
      font-size: 16px;
    }

    span.dx-field-item-label-text {
      font-weight: 500;
    }

    .info-panel {
      display: flex;
      flex-direction: row;
      align-items: center;

      .label {
        margin-right: 10px;
        color: #666666;
      }

      .value {
        margin-right: 5px;

        font-weight: 500;
      }

      .ok {
        color: rgb(0,128,0);
      }

      .warning {
        color: rgb(255,165,0);
      }

      .error {
        color: rgb(255,0,0);
      }

      .not-serviced {
        color: rgb(170,170,170);
      }
    }
  }
}

.info-device {
  background-color: rgb(255, 252, 229);
}

.group {
  &:first-child {
    margin-bottom: 5px;
    padding-bottom: 5px;

    border-bottom: 1px solid #ccc;
  }

  .field {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin-bottom: 5px;

      .label {
        margin-right: 10px;
        width: 40%;

        color: #666666;
      }

      .value {
        display: flex;
        flex-direction: row;

        font-weight: 500;

        .optional {
          width: 80px;
        }
      }
  }
}
</style>
